import { atom } from 'jotai';
import type { ConversationData } from '@/types/api';
import { conversationService } from '@/services/conversationService';

// Holds the last-fetched conversation data (url + source name)
export const conversationDataAtom = atom<ConversationData | null>(null);

// Loading / error state for the async operations
export const conversationLoadingAtom = atom<boolean>(false);
export const conversationErrorAtom = atom<string | null>(null);

// Selector atoms for convenience
export const conversationRoomUrlAtom = atom(
  get => get(conversationDataAtom)?.conversationUrl ?? null
);

export const conversationSourceNameAtom = atom(
  get => get(conversationDataAtom)?.sourceName ?? null
);

export const conversationIdAtom = atom(
  get => get(conversationDataAtom)?.conversationId ?? null
);

// Write-atom to fetch conversation data and populate `conversationDataAtom`.
export const fetchConversationAtom = atom(
  null,
  async (
    _get,
    set,
    params: {
      id: string;
      needsRecording: boolean;
    }
  ) => {
    const { id, needsRecording } = params;
    set(conversationLoadingAtom, true);
    set(conversationErrorAtom, null);
    try {
      const response = await conversationService.startInterview(
        id,
        needsRecording
      );
      const conversationId = response.conversationUrl.replace(
        'https://tavus.daily.co/',
        ''
      );
      const data: ConversationData = {
        ...response,
        conversationId,
      };
      set(conversationDataAtom, data);
      return data;
    } catch (err: any) {
      const msg = err?.message ?? 'Failed to fetch conversation';
      set(conversationErrorAtom, msg);
      set(conversationDataAtom, null);
      throw err;
    } finally {
      set(conversationLoadingAtom, false);
    }
  }
);

// Write-atom to stop an in-progress interview and update stored data.
export const stopConversationAtom = atom(
  null,
  async (_get, set, conversationId: string) => {
    set(conversationLoadingAtom, true);
    set(conversationErrorAtom, null);
    try {
      await conversationService.stopInterview(conversationId);
    } catch (err: any) {
      const msg = err?.message ?? 'Failed to stop conversation';
      set(conversationErrorAtom, msg);
      throw err;
    } finally {
      set(conversationLoadingAtom, false);
    }
  }
);
